import { Component, Output, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable , combineLatest, filter, of, startWith, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';

import { AuthService } from '~core/services/auth.service';
import { ClientModeService } from '~core/services/client-mode.service';
import { CompanyService } from '~core/services/company.service';
import { ExternalUser, InternalUser } from '~modules/auth/types/user.types';
import ProjectsRepository from '~modules/project/store/projects/projects.repository';
import { MaintenanceRepository } from '~shared/store/maintenance/maintenance.repository';
import { UserRole, UserType } from '~shared/enums/user.enum';

import { HeaderMenuItem } from './header.types';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
	@Input() menuItems: HeaderMenuItem[] = [];
	@Output() sidenavToggle = new EventEmitter<void>();

	public currentUser$: Observable<InternalUser | ExternalUser>
	public activeProjectId$: Observable<string | null>;
	public showInternalTools$: Observable<boolean>;
	public showMaintenanceBadge$: Observable<boolean>;
	public clientMode$: Observable<boolean>;
	/** Object URL of the professional's logo while client mode is on; null shows nothing. */
	public clientLogoUrl: string | null = null;

	private componentDestroyed$ = new Subject<void>();

	constructor(
		private readonly authService: AuthService,
		private readonly router: Router,
		private readonly projectsRepository: ProjectsRepository,
		private readonly maintenanceRepository: MaintenanceRepository,
		private readonly clientModeService: ClientModeService,
		private readonly companyService: CompanyService,
	) {}

	// Logo-only chrome (no menu / user actions) on the terms page and the public
	// maintenance page — but not on the internal /maintenance-admin controls.
	public get logoOnly(): boolean {
		const path = this.router.url.split('?')[0];
		return path.includes('algemene_voorwaarden') || path === '/maintenance';
	}

	public ngOnInit(): void {
		this.currentUser$ = this.authService.currentUser$;
		this.clientMode$ = this.clientModeService.active$;
		this.clientModeService.accountId$
			.pipe(
				switchMap((accountId) => accountId ? this.companyService.getLogo(accountId) : of(null)),
				takeUntil(this.componentDestroyed$)
			)
			.subscribe((url) => this.setClientLogoUrl(url));

		this.activeProjectId$ = this.projectsRepository.activeProject$.pipe(
			map(project => project?.id || null)
		);

		const excludedPaths = ['/auth/', '/algemene_voorwaarden'];
		this.showInternalTools$ = this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map(event => event.url),
			startWith(this.router.url),
			// /auth/account is an authenticated page where an internal user inspects a
			// switched-to account, so the account-switcher must stay; only the rest of
			// the auth flow (login, register, password, …) hides the internal tools.
			map(url => url.includes('/auth/account') || !excludedPaths.some(path => url.includes(path)))
		);

		// Internal admins get a clear "maintenance is on" signal in the header.
		this.showMaintenanceBadge$ = combineLatest([
			this.currentUser$,
			this.maintenanceRepository.isInMaintenance$,
		]).pipe(
			map(([user, inMaintenance]) =>
				inMaintenance
				&& user?.userType === UserType.INTERNAL
				&& user?.userRole === UserRole.ADMIN
			)
		);
	}

	public ngOnDestroy(): void {
		this.setClientLogoUrl(null);
		this.componentDestroyed$.next();
		this.componentDestroyed$.complete();
	}

	private setClientLogoUrl(url: string | null): void {
		if (this.clientLogoUrl) {
			URL.revokeObjectURL(this.clientLogoUrl);
		}
		this.clientLogoUrl = url;
	}
}
